/*
 * travelApi — the single place the customer site talks to the 513Travel
 * backend (Phase 2B). Centralizes:
 *   - the API base (VITE_API_BASE_URL via runtimeConfig)
 *   - camelCase form  ->  snake_case API field mapping
 *   - graceful, typed errors with friendly user-facing messages
 *
 * Backend contract (https://api.513travel.com):
 *   POST /travel/requests          -> { reference, status }
 *   GET  /travel/requests/{ref}     -> { reference, status, status_label, active_step, next_step, created_at }
 *   POST /travel/contact            -> { ok: true }
 *
 * If the backend isn't configured (API_BASE_URL empty) or is unreachable, the
 * helpers throw an ApiError whose .userMessage is safe to show in the UI. We
 * NEVER fabricate a reference on failure.
 */
import { API_BASE_URL, HAS_BACKEND, SUPPORT_EMAIL } from "./runtimeConfig.js";

const REQUEST_TIMEOUT_MS = 15000;

const FRIENDLY = {
  unconfigured: "Online submission is temporarily unavailable.",
  network: "We couldn't reach our system. Please check your connection and try again.",
  server: "Something went wrong on our side. Please try again in a moment.",
  validation: "Please review the form and try again.",
  notfound: "No request found for that reference.",
};

export class ApiError extends Error {
  constructor(kind, status) {
    super(kind);
    this.name = "ApiError";
    this.kind = kind; // "unconfigured" | "network" | "server" | "validation" | "notfound"
    this.status = status || 0;
    this.userMessage = FRIENDLY[kind] || FRIENDLY.server;
    this.supportEmail = SUPPORT_EMAIL;
  }
}

async function request(method, path, body) {
  if (!HAS_BACKEND) throw new ApiError("unconfigured");

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  let res;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch {
    // Network failure, CORS rejection, or timeout/abort.
    throw new ApiError("network");
  } finally {
    clearTimeout(timer);
  }

  if (res.ok) {
    try {
      return await res.json();
    } catch {
      return {};
    }
  }
  if (res.status === 404) throw new ApiError("notfound", 404);
  if (res.status === 422 || res.status === 400) throw new ApiError("validation", res.status);
  throw new ApiError("server", res.status);
}

// Drop empty-string values so optional date/text fields land as null, not "".
function clean(value) {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" ? null : trimmed;
  }
  return value;
}

/* ---- Plan a Trip ---- */
// `form` is the PlanTrip form object plus a computed `fullWhatsAppNumber`.
export async function createTripRequest(form) {
  const body = {
    services: Array.isArray(form.services) ? form.services : [],
    from_location: clean(form.fromLocation),
    destination: clean(form.destination),
    departure_date: clean(form.departureDate),
    return_date: clean(form.returnDate),
    flexible_dates: form.flexibleDates ?? null, // "yes"/"no"; backend converts
    adults: form.adults === "" || form.adults == null ? null : Number(form.adults),
    children: form.children === "" || form.children == null ? null : Number(form.children),
    travel_class: clean(form.travelClass),
    budget: clean(form.budget),
    currency: clean(form.currency),
    language: clean(form.language),
    mood: clean(form.mood),
    full_name: clean(form.fullName),
    email: clean(form.email),
    country_code: clean(form.countryCode),
    whatsapp_number: clean(form.whatsappNumber),
    full_whatsapp_number: clean(form.fullWhatsAppNumber),
    residence: clean(form.residence),
    notes: clean(form.notes),
    consent: Boolean(form.consent),
  };
  const data = await request("POST", "/travel/requests", body);
  return { reference: data.reference, status: data.status }; // server-issued only
}

/* ---- Track Trip ---- */
// Maps the public-safe API shape to the existing TrackTrip UI shape.
export async function getTripStatus(reference) {
  const data = await request("GET", `/travel/requests/${encodeURIComponent(reference)}`);
  return {
    reference: data.reference,
    status: data.status,
    statusLabel: data.status_label,
    activeStep: data.active_step,
    nextStep: data.next_step,
    createdAt: data.created_at,
  };
}

/* ---- Contact ---- */
// `form` is the Contact form object plus a computed `fullWhatsAppNumber`.
export async function sendContactMessage(form) {
  const body = {
    full_name: clean(form.fullName),
    email: clean(form.email),
    country_code: clean(form.countryCode),
    whatsapp_number: clean(form.whatsappNumber),
    full_whatsapp_number: clean(form.fullWhatsAppNumber),
    subject: clean(form.subject),
    message: clean(form.message),
    preferred_contact: clean(form.preferred),
    consent: Boolean(form.consent),
  };
  const data = await request("POST", "/travel/contact", body);
  return { ok: Boolean(data.ok) };
}
